import type { CSSProperties, FC, KeyboardEvent, MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dropdown, Menu } from 'antd';
import type { DropdownProps } from 'antd';
import { TbBuildingBank, TbWorld } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { exchangeVariantQueryOptions } from '@/queries';
import type { FmpExchangeVariant } from '@/queries';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import BaseSkeleton from '@/shared/components/skeletons/BaseSkeleton';

import SmallTag from '../SmallTag';

interface TickerExchangeTagProps {
    assetType?: 'company' | 'fund';
    companyName?: string | null;
    compact?: boolean;
    exchange?: string | null;
    exchangeStyle?: CSSProperties;
    placement?: DropdownProps['placement'];
    styles?: CSSProperties;
    symbol?: string | null;
    symbolStyle?: CSSProperties;
    website?: string | null;
}

interface TickerExchangeMenuProps extends Pick<
    TickerExchangeTagProps,
    'assetType' | 'companyName' | 'exchange' | 'symbol' | 'website'
> {
    onClose: () => void;
}

const normalize = (value?: string | null) => value?.trim().toUpperCase() ?? '';

const exchangeCode = (variant: FmpExchangeVariant) =>
    variant.exchangeShortName ?? variant.exchange ?? variant.stockExchange ?? 'Other';

const exchangeName = (variant: FmpExchangeVariant) =>
    variant.stockExchange ?? variant.exchange ?? variant.exchangeShortName ?? 'Exchange unavailable';

const listingKey = (variant: FmpExchangeVariant) => `${normalize(exchangeCode(variant))}:${normalize(variant.symbol)}`;

const TickerExchangeMenu: FC<TickerExchangeMenuProps> = ({
    assetType = 'company',
    companyName,
    exchange,
    onClose,
    symbol,
    website,
}) => {
    const { token } = useAntDesignTheme();
    const [search, setSearch] = useState('');
    const normalizedSymbol = symbol?.trim() ?? '';
    const variantsQuery = useQuery(exchangeVariantQueryOptions.bySymbol(normalizedSymbol));
    const variants = useMemo(() => {
        if (!normalizedSymbol) {
            return [];
        }

        const currentVariant: FmpExchangeVariant = {
            exchange,
            exchangeShortName: exchange,
            name: companyName,
            symbol: normalizedSymbol,
        };
        const variantsByKey = new Map<string, FmpExchangeVariant>([[listingKey(currentVariant), currentVariant]]);

        variantsQuery.data?.forEach((variant) => variantsByKey.set(listingKey(variant), variant));

        return [...variantsByKey.values()].sort((left, right) => {
            const leftCurrent = listingKey(left) === listingKey(currentVariant);
            const rightCurrent = listingKey(right) === listingKey(currentVariant);

            if (leftCurrent !== rightCurrent) {
                return leftCurrent ? -1 : 1;
            }

            return `${exchangeCode(left)}:${left.symbol}`.localeCompare(`${exchangeCode(right)}:${right.symbol}`);
        });
    }, [companyName, exchange, normalizedSymbol, variantsQuery.data]);
    const filteredVariants = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) {
            return variants;
        }

        return variants.filter((variant) =>
            [variant.name, variant.symbol, variant.type, exchangeCode(variant), exchangeName(variant)].some((value) =>
                value?.toLowerCase().includes(query),
            ),
        );
    }, [search, variants]);

    return (
        <DropdownPanel
            footer={<LearnMoreFooter topic='ticker symbols and global exchange listings' variant='dropdown' />}
            maxHeight='min(28rem, 70vh)'
            search={{
                ariaLabel: 'Search exchange listings',
                placeholder: 'Search Tickers Or Exchanges...',
                setValue: setSearch,
                value: search,
            }}
            width={16 * 25}
        >
            <DropdownMenuLabel
                extra={
                    <small style={{ color: token.colorTextQuaternary }}>
                        {filteredVariants.length.toLocaleString()}
                    </small>
                }
                icon={<TbWorld size='0.875rem' />}
            >
                Global Listings
            </DropdownMenuLabel>
            <DropdownPanelMenu selectable={false}>
                {variantsQuery.isPending && !variantsQuery.data ? (
                    <Menu.Item disabled key='loading-listings'>
                        <BaseSkeleton paragraph={{ rows: 4 }} title={false} />
                    </Menu.Item>
                ) : variantsQuery.isError && variants.length <= 1 ? (
                    <EmptySection
                        description='Exchange listings could not be loaded. Please try again shortly.'
                        height='10rem'
                        icon={<TbBuildingBank size='1.25rem' />}
                        label='Listings Unavailable'
                    />
                ) : filteredVariants.length ? (
                    filteredVariants.map((variant) => {
                        const code = exchangeCode(variant);
                        const current =
                            normalize(code) === normalize(exchange) && normalize(variant.symbol) === normalize(symbol);

                        return (
                            <Menu.Item key={listingKey(variant)} onClick={onClose}>
                                <a
                                    href={`/search/${assetType === 'fund' ? 'funds' : 'companies'}/${encodeURIComponent(variant.symbol)}/overview`}
                                    style={{ color: 'inherit' }}
                                >
                                    <DropdownOptionContent
                                        avatar={{
                                            icon: <TbBuildingBank size='0.875rem' />,
                                            website: website ?? undefined,
                                        }}
                                        metadata={[
                                            exchangeName(variant),
                                            variant.type ?? (current ? 'Current listing' : undefined),
                                        ]}
                                        selected={current}
                                        singleLine
                                        title={
                                            <>
                                                <strong>{code}</strong>: {variant.symbol}
                                            </>
                                        }
                                    />
                                </a>
                            </Menu.Item>
                        );
                    })
                ) : (
                    <EmptySection
                        description='No ticker or exchange matches your search.'
                        height='10rem'
                        icon={<TbWorld size='1.25rem' />}
                        label='No Listings Found'
                    />
                )}
            </DropdownPanelMenu>
        </DropdownPanel>
    );
};

const TickerExchangeTagContent: FC<TickerExchangeTagProps> = ({
    assetType,
    companyName,
    compact = false,
    exchange,
    exchangeStyle,
    placement = 'bottomLeft',
    styles,
    symbol,
    symbolStyle,
    website,
}) => {
    const [open, setOpen] = useState(false);

    const handleTriggerClick = (event: MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation();
    };

    const handleTriggerKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ArrowDown') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setOpen(true);
    };

    return (
        <Dropdown
            arrow={false}
            destroyPopupOnHide
            onOpenChange={setOpen}
            open={open}
            popupRender={() =>
                open ? (
                    <TickerExchangeMenu
                        assetType={assetType}
                        companyName={companyName}
                        exchange={exchange}
                        onClose={() => setOpen(false)}
                        symbol={symbol}
                        website={website}
                    />
                ) : (
                    <span />
                )
            }
            placement={placement}
            trigger={['hover']}
        >
            <span
                aria-expanded={open}
                aria-haspopup='menu'
                aria-label={`View global listings for ${exchange || 'unknown exchange'}: ${symbol || 'unknown ticker'}`}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
                role='button'
                style={{ cursor: 'help', display: 'inline-flex' }}
                tabIndex={0}
            >
                <SmallTag
                    compact={compact}
                    message={
                        <>
                            <span style={{ fontWeight: 500, ...exchangeStyle }}>{exchange || 'N/A'}:</span>&nbsp;
                            <span style={symbolStyle}>{symbol || 'N/A'}</span>
                        </>
                    }
                    styles={{ cursor: 'help', marginLeft: 0, ...styles }}
                />
            </span>
        </Dropdown>
    );
};

const TickerExchangeTagWithLookup: FC<TickerExchangeTagProps> = (props) => {
    const normalizedSymbol = props.symbol?.trim() ?? '';
    const variantsQuery = useQuery(exchangeVariantQueryOptions.bySymbol(normalizedSymbol));
    const currentVariant = variantsQuery.data?.find(
        (variant) => normalize(variant.symbol) === normalize(normalizedSymbol),
    );
    const resolvedExchange =
        currentVariant?.exchangeShortName ?? currentVariant?.exchange ?? currentVariant?.stockExchange ?? null;

    return <TickerExchangeTagContent {...props} exchange={resolvedExchange} />;
};

const TickerExchangeTag: FC<TickerExchangeTagProps> = (props) =>
    props.exchange?.trim() || !props.symbol?.trim() ? (
        <TickerExchangeTagContent {...props} />
    ) : (
        <TickerExchangeTagWithLookup {...props} />
    );

export default TickerExchangeTag;
